/*
 * Publish workspace and Export dialog (W2-09 publish-import; DESIGN.md §4.16). Design tokens only. Every status
 * pairs an icon with a word (colour is never the only signal); long lists scroll inside their panel.
 */

.panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: auto;
  background: var(--color-surface-1);
  color: var(--color-text);
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3) var(--space-4);
}

.muted {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

/* ---------- check summary ---------- */

.severityList,
.issueList,
.noteList,
.statsList,
.fileList,
.switchList,
.chips {
  list-style: none;
  margin: 0;
  padding: 0;
}

.severityList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.severityRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--control-h-md);
  font-size: var(--text-md);
}

.severityRow > svg {
  flex: none;
}

.severityRow[data-severity='fatal'][data-count='nonzero'] > svg {
  color: var(--color-severity-fatal);
}

.severityRow[data-severity='error'][data-count='nonzero'] > svg {
  color: var(--color-severity-error);
}

.severityRow[data-severity='warning'][data-count='nonzero'] > svg {
  color: var(--color-severity-warning);
}

.severityRow[data-severity='tip'][data-count='nonzero'] > svg {
  color: var(--color-severity-tip);
}

.severityRow[data-count='zero'] > svg {
  color: var(--color-success);
}

.severityText {
  flex: 1;
  min-width: 0;
}

.subTitle {
  margin: var(--space-2) 0 var(--space-1);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-text-muted);
}

.issueList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.issue {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
}

.issue > svg {
  flex: none;
  margin-top: 2px;
}

.issue[data-severity='fatal'] {
  background: var(--color-severity-fatal-muted);
}

.issue[data-severity='fatal'] > svg {
  color: var(--color-severity-fatal);
}

.issue[data-severity='error'] > svg {
  color: var(--color-severity-error);
}

.issue[data-severity='warning'] > svg {
  color: var(--color-severity-warning);
}

.issue[data-severity='tip'] > svg {
  color: var(--color-severity-tip);
}

.issueMain {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
}

.issueWord {
  font-size: var(--text-2xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-text-muted);
}

.issueMessage {
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.issueWhy {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.issueActions {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-1);
}

/* ---------- compatibility ---------- */

.compatList {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.compatRow {
  display: grid;
  grid-template-columns: minmax(80px, 30%) 1fr;
  gap: var(--space-2);
  align-items: start;
}

.compatName {
  margin: 0;
  font-weight: var(--weight-medium);
  font-size: var(--text-md);
}

.compatValue {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.compatStatus {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  flex-wrap: wrap;
  font-size: var(--text-md);
}

.compatStatus > svg {
  flex: none;
}

.compatStatus[data-status='ok'] > svg,
.compatStatus[data-status='notes'] > svg {
  color: var(--color-success);
}

.compatStatus[data-status='differs'] > svg {
  color: var(--color-warning);
}

.compatStatus[data-status='blocked'] > svg {
  color: var(--color-severity-fatal);
}

.noteList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding-left: var(--space-4);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.noteList > li {
  list-style: disc;
  overflow-wrap: anywhere;
}

/* ---------- stats ---------- */

.statsList {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-4);
  font-size: var(--text-md);
}

.exportState {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin-top: var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.exportState[data-changed='true'] > svg {
  color: var(--color-warning);
}

.exportState[data-changed='false'] > svg {
  color: var(--color-success);
}

/* ---------- package form ---------- */

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.formHeader {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
}

.thumb {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  width: 160px;
}

.thumbFrame {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
  text-align: center;
}

.thumbFrame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.headerFields {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.row2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--space-2);
  /* Labels on one line whatever help text a field carries below (UX-080). */
  align-items: start;
}

.row2 > [data-field='difficulty'] > div {
  /* A stacked number field pads its top; the text fields beside it do not (labels on one line, UX-080). */
  padding-top: 0;
}

.fieldStack {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.previewRow {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  flex-wrap: wrap;
}

/* The fields keep a usable width; the play button sits right next to "To" (UX-080). */
.previewRow > [data-focus-field] {
  min-width: 100px;
}

/* The primary export actions stay in view at the bottom of the panel (UX-080). */
.actionBar {
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-top: var(--border-width) solid var(--color-divider);
  background: var(--color-surface-1);
}

.actionBar > .muted {
  margin-left: auto;
}

.problem {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-severity-fatal-muted);
  font-size: var(--text-sm);
}

.problem > svg {
  flex: none;
  margin-top: 2px;
  color: var(--color-severity-fatal);
}

.problem > span {
  flex: 1;
  min-width: 0;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-0);
  padding: 0 var(--space-0) 0 var(--space-2);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface-2);
  font-size: var(--text-sm);
}

/* ---------- export section ---------- */

.radioList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  border: none;
}

.radioLegend {
  padding: 0;
  margin-bottom: var(--space-1);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.radio {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.radio:hover {
  background: var(--color-surface-hover);
}

.radio input {
  margin-top: 3px;
  accent-color: var(--color-accent);
}

.radio input:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.radioText {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  min-width: 0;
}

.radioLabel {
  font-size: var(--text-md);
}

.radioHelp {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.buttonRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.advanced {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-1) 0 var(--space-2);
}

.readOnly {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  font-size: var(--text-md);
}

.warning {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-warning-muted);
  font-size: var(--text-sm);
}

.warning > svg {
  flex: none;
  margin-top: 2px;
  color: var(--color-warning);
}

.warning > div {
  flex: 1;
  min-width: 0;
}

.noticeBody {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-1);
}

/* ---------- export dialog ---------- */

.dialogChoices {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  align-items: flex-end;
}

/* The target keeps its full labels; the profile takes the rest of the row (or wraps below). */
.dialogChoices > :first-child {
  flex: 0 0 auto;
}

.dialogChoices > :not(:first-child) {
  flex: 1 1 220px;
  min-width: 0;
}

/* Folded technical details (UX-077): the summary line is a button-like row. */
.disclosure {
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
}

.disclosure > summary {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  cursor: pointer;
  color: var(--color-text-muted);
}

.disclosure > summary > svg {
  flex: none;
}

/* Disclosure chevron (the native marker is reset away). */
.disclosure > summary::before {
  content: '';
  flex: none;
  width: 6px;
  height: 6px;
  margin: 0 var(--space-1) 0 2px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(-45deg);
}

.disclosure[open] > summary::before {
  transform: rotate(45deg);
}

.disclosure[data-notice] > summary > svg {
  color: var(--color-warning);
}

.disclosure[open] > summary {
  border-bottom: var(--border-width) solid var(--color-divider);
}

.disclosureBody {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-1);
  padding: var(--space-2);
}

.dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.dialogSection {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.sectionLabel {
  margin: 0;
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-text-muted);
}

.preflightCounts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-4);
  font-size: var(--text-md);
}

.preflightCount {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

.preflightCount[data-kind='blocking'][data-zero='true'] > svg {
  color: var(--color-success);
}

.preflightCount[data-kind='blocking'][data-zero='false'] > svg {
  color: var(--color-severity-fatal);
}

.preflightCount[data-kind='notes'] > svg {
  color: var(--color-text-muted);
}

.preflightCount[data-kind='notes'][data-warn='true'] > svg {
  color: var(--color-warning);
}

.preflightCount[data-kind='checking'] > svg,
.checkStatus > svg {
  color: var(--color-text-muted);
}

/* A checker pass is running (the Check summary and the Export dialog's preflight). */
.checkStatus {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

/*
 * Idle, the status regions stay in the accessibility tree (so screen readers announce "Checking the chart..." when it
 * appears) but take no place in the flex layout and its gaps.
 */
.checkStatus:empty,
.preflightCount[data-kind='checking']:empty {
  position: absolute;
}

@media (prefers-reduced-motion: no-preference) {
  .preflightCount[data-kind='checking'] > svg,
  .checkStatus > svg {
    animation: publish-spin 1s linear infinite;
  }
}

@keyframes publish-spin {
  to {
    transform: rotate(360deg);
  }
}

.mono {
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.fileList {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
}

.fileList > li {
  font-family: var(--font-mono);
}

.diffs {
  max-height: 180px;
  overflow: auto;
  margin: 0;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.failure {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-severity-error-muted);
}

.failure > svg {
  flex: none;
  margin-top: 2px;
  color: var(--color-severity-error);
}

.failure > div {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.failureTitle {
  margin: 0;
  font-weight: var(--weight-semibold);
  font-size: var(--text-md);
}

.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  width: 100%;
}

.footerStart {
  margin-right: auto;
}
